import { html, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import style from "./shell.css?inline";

export type SecondaryNavCompactToggleEvent = CustomEvent<{ compact: boolean }>;

const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

@customElement("eko-shell")
class Shell extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @property({ reflect: true, type: Boolean, attribute: "secondary-nav" }) hasSecondaryNav: boolean =
    false;
  @property({ reflect: true }) menu?: "primary" | "secondary";
  @property({ reflect: true, type: Boolean, attribute: "secondary-nav-compact" })
  secondaryNavCompact: boolean = false;

  private toggleSecondaryNav() {
    this.secondaryNavCompact = !this.secondaryNavCompact;
    this.dispatchEvent(
      new CustomEvent("secondary-nav:compact:toggle", {
        bubbles: true,
        detail: { compact: this.secondaryNavCompact },
      }) as SecondaryNavCompactToggleEvent
    );
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener("shell:menu:toggle", () => {
      if (this.menu) {
        this.menu = undefined;
        return;
      }

      const secondary = this.shadowRoot?.querySelector<HTMLSlotElement>(`slot[name="secondary"]`);

      secondary?.assignedNodes({ flatten: true }).length
        ? (this.menu = "secondary")
        : (this.menu = "primary");
    });

    this.addEventListener("shell:current:click", () => {
      if (isMobile()) {
        this.menu = "primary";
      }
    });
  }

  render() {
    return html`<div class="eko-shell__top" part="top">
        <header class="eko-shell__header" part="header">
          <div class="eko-shell__header-left" part="header-left">
            <div class="eko-shell__header-logo" part="header-logo">
              <slot name="logo"></slot>
            </div>
            <div class="eko-shell__header-organization" part="header-organization">
              <slot name="organization"></slot>
            </div>
          </div>
          <div class="eko-shell__header-search" part="header-search">
            <slot name="search"></slot>
          </div>
          <div class="eko-shell__header-right" part="header-right">
            <div class="eko-shell__header-actions" part="header-actions">
              <slot name="actions"></slot>
            </div>
            <div class="eko-shell__header-notifications" part="header-notifications">
              <slot name="notifications"></slot>
            </div>
            <div class="eko-shell__header-user" part="header-user">
              <slot name="user"></slot>
            </div>
            <div class="eko-shell__header-hamburger" part="header-hamburger">
              <slot name="hamburger"></slot>
            </div>
          </div>
        </header>
        <slot name="header-content"></slot>
      </div>
      <div class="eko-shell__body" part="body">
        <div class="eko-shell__sidebar" part="sidebar">
          <slot class="eko-shell__sidebar-primary" name="primary"></slot>
          <div class="eko-shell__sidebar-secondary" part="sidebar-secondary">
            <slot name="secondary"></slot>

            ${this.hasSecondaryNav
              ? html`<button
                  class="eko-shell__sidebar-layout-button"
                  part="sidebar-layout-button"
                  @click=${this.toggleSecondaryNav}
                  aria-label="${this.secondaryNavCompact
                    ? "Expand secondary navigation"
                    : "Collapse secondary navigation"}">
                  <eko-icon
                    aria-hidden="true"
                    name="${this.secondaryNavCompact
                      ? "chevron-right-double"
                      : "chevron-left-double"}"></eko-icon>
                </button>`
              : nothing}
          </div>
        </div>
        <div class="eko-shell__content" part="content">
          <div class="eko-shell__content-main" part="content-main">
            <slot></slot>
          </div>
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-shell": Shell;
  }

  interface DocumentEventMap {
    "secondary-nav:compact:toggle": SecondaryNavCompactToggleEvent;
  }
}

export default Shell;
